import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Tippy from "@tippyjs/react";
import { debounce } from "lodash";
import { Slider, useMediaQuery } from "@mui/material";
import { ChevronRight } from "@material-ui/icons";
import { Button, Input, Modal, Typography } from "design_system/src";
import { ReactComponent as AddCircle } from "design_system/src/static/icons/add-circle.svg";
import { ReactComponent as WarningCircle } from "design_system/src/static/icons/warning-circle.svg";
import { ISalePrices, TVehicle } from "models/inventory/interfaces/vehicle.interface";
import { VehicleStatusEnum } from "models/inventory/enums/vehicle.enums";
import { clearApplicationPartnership, setApplicationLoanTime, setApplicationPartnership, setApplicationVehicle } from "store/actions/application";
import { priceFormatFinancial, prices } from "helpers/prices";
import PlanSelectionBox from "components/planSelectionBox/PlanSelectionBox";
import useAlliance from "hooks/useAlliance";

const titles: any = {
  "52": "Facilidad",
  "78": "Oportunidad",
  "104": "Comodidad",
  "156": "Seguridad",
};

interface ISelectPlanArr {
  title: string;
  weeks: number;
  weeklyAmount: string;
  weeklyAmountValue: number;
  noDiscountAmount?: string;
  depositAmount: number;
}

interface SummaryPaymentProps {
  vehicle?: TVehicle;
  selectedWeeksProps?: number
}

const calculateDeposit = (loanAmount: number): number => {
  const DEPOSIT_RULES = {
    LOW: { limit: 45000, value: 2000 },
    MID: { limit: 60000, value: 2500 },
    HIGH: { value: 3000 },
  };

  if (loanAmount < DEPOSIT_RULES.LOW.limit) return DEPOSIT_RULES.LOW.value;
  if (loanAmount <= DEPOSIT_RULES.MID.limit) return DEPOSIT_RULES.MID.value;
  return DEPOSIT_RULES.HIGH.value;
};

interface SummaryBoxProps {
  title: string;
  deposit: number;
  quotaValue: number;
}

const SummaryBox: React.FC<SummaryBoxProps> = ({
  title,
  deposit,
  quotaValue,
}) => {
  const tooltipContent = () => (
    <div className="display_flex flex_gap_xxs p_sm">
      <Typography
        scale="small"
        weight="400"
      >
        Este monto de dinero se abona junto con la primera cuota
        y funciona como una garantía. Una vez que se ha pagado
        la totalidad del crédito, se devuelve.
      </Typography>
    </div>
  );

  return (
    <div>
      <Typography
        scale="medium"
        weight="600"
        className="m_t_md m_b_xs"
      >
        Resumen Plan {title}
      </Typography>
      <div
        className="bg_green_100 display_flex flex_col flex_gap_md p_md br_xs m_t_md"
      >
        <div className="display_flex flex_align_center flex_justify_between">
          <div className="display_flex flex_align_center">
            <Typography scale="medium" weight="400">
              Depósito en garantía
            </Typography>
            <Tippy content={tooltipContent()} className="bg_neutral_0 shadow_hard">
              <div className="border_solid border_1 border_neutral_700 br_circle dim_md cursor_pointer pos_relative m_l_xs">
                <Typography
                  weight="600"
                  scale="xxsmall"
                  textColor="neutral_700"
                  className=" pos_absolute center"
                >
                  i
                </Typography>
              </div>
            </Tippy>
          </div>
          <Typography
            scale="medium"
            weight="400"
            style={{ whiteSpace: "nowrap" }}
          >
            ${deposit.toLocaleString("en-US")} MXN
          </Typography>
        </div>
        <div className="display_flex flex_align_center flex_justify_between">
          <Typography scale="medium" weight="400">
            Pago semanal
          </Typography>
          <Typography
            scale="medium"
            weight="400"
            style={{ whiteSpace: "nowrap" }}
          >
            ${quotaValue.toLocaleString("en-US")} MXN
          </Typography>
        </div>
        <div className="display_flex flex_align_center flex_justify_between">
          <Typography scale="large" scaleMobile="medium" weight="600" className="">
            TOTAL (Primer pago)
          </Typography>
          <Typography
            scale="large"
            scaleMobile="medium"
            weight="600"
            style={{ whiteSpace: "nowrap" }}
          >
            ${(quotaValue + deposit).toLocaleString("en-US")} MXN
          </Typography>
        </div>
      </div>
      <div className="bg_primary_25 border_solid border_1 border_primary_300 br_xs p_sm m_t_sm display_flex flex_align_start flex_gap_sm">
        <WarningCircle className="text_primary_300" style={{ stroke: "none" }} />
        <Typography scale="small" weight="400" textColor="neutral_800">
          En el primer pago semanal se descontarán los <strong>$150 MXN</strong> entregados como apartado de la motocicleta.
        </Typography>
      </div>
    </div>
  );
};

const SummaryPayment: React.FC<SummaryPaymentProps> = ({ vehicle, selectedWeeksProps }) => {
  const matchesXS = useMediaQuery("(min-width:600px)");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, allianceUrl } = useAlliance();

  const [initialPaymentAmount, setInitialPaymentAmount] = useState<number>(0);
  const [advancedMoney, setAdvancedMoney] = useState<number>(0);
  const [OpenAM, setOpenAM] = useState<boolean>(false);
  const [Opened, setOpened] = useState<boolean>(false);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState<number>(0);
  const [selectPlanArr, setSelectPlanArr] = useState<ISelectPlanArr[]>([]);
  const selectedWeeks = useMemo<number>(() => {
    return selectPlanArr[selectedPlanIndex]?.weeks ?? 52;
  }, [selectedPlanIndex, selectPlanArr]);

  const min = 0;
  const max = 20000;
  const step = 500;

  useEffect(() => {
    if (selectedWeeksProps) {
      const index = selectPlanArr.findIndex(({ weeks }) => weeks === selectedWeeksProps);
      setSelectedPlanIndex(index >= 0 ? index : 0);
    }
  }, []);

  const handleInputAMChange = useCallback(
    debounce((valuetosaved: number) => {
      if (valuetosaved >= min && valuetosaved <= max) {
        setInitialPaymentAmount(valuetosaved);
      }
    }, 300), // 300ms de retraso
    []
  );

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setAdvancedMoney(newValue as number);
    handleInputAMChange(newValue as number);
  };

  const handleButtonSum = () => {
    if (advancedMoney >= min && advancedMoney < max) {
      setAdvancedMoney(advancedMoney + 500);
      handleInputAMChange(advancedMoney + 500);
    }
  };

  const handleButtonRest = () => {
    if (advancedMoney > min && advancedMoney <= max) {
      setAdvancedMoney(advancedMoney - 500);
      handleInputAMChange(advancedMoney - 500);
    }
  };

  const onInputChange  = (event: React.ChangeEvent<HTMLInputElement>) => {
    const valuetosaved = Number(event.target.value);
    setAdvancedMoney(valuetosaved);
    if (valuetosaved < min || valuetosaved > max) {
      return;
    }
    handleInputAMChange(valuetosaved);
  };

  const BoxShadow =
    "0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.13),0 0 0 1px rgba(0,0,0,0.02)";

  const handleModalAM = () => {
    setOpenAM(!OpenAM);
  };

  const handleButtonAM = () => {
    if (!Opened) {
      setOpenAM(!OpenAM);
    }
    setOpened(true);
  };

  const openFinancialForm = () => {
    if (vehicle) {
      dispatch(setApplicationVehicle(vehicle));
      dispatch(clearApplicationPartnership());
      dispatch(setApplicationLoanTime(selectedWeeks));
      navigate(allianceUrl ? `/${allianceUrl}/financia-tu-moto` : "/financia-tu-moto");
    }
  };

  useEffect(() => {
    if (vehicle) {
      vehicle.setAdvancedMoney(Number(initialPaymentAmount));
      dispatch(setApplicationVehicle(vehicle));
      dispatch(setApplicationLoanTime(selectedWeeks));
    }
  }, [initialPaymentAmount]);

  useEffect(() => {
    if (vehicle && vehicle.status !== VehicleStatusEnum.AVAILABLE) {
      if (selectedWeeksProps) {
        const index = selectPlanArr.findIndex(({ weeks }) => weeks === selectedWeeksProps);
        setSelectedPlanIndex(index >= 0 ? index : 0);
      } else {
        setSelectedPlanIndex(vehicle.salePrices.length - 1);
      }
    }
  }, [vehicle]);

  const updateSelectPlanArr = async () => {
    const salePrices = vehicle?.salePrices ?? [];
    const PlanArr = [];
    // eslint-disable-next-line no-plusplus
    for (let i=0; i<salePrices.length; i++) {
      const { weeks } = salePrices[i];
      const title = titles[weeks];
      // eslint-disable-next-line no-await-in-loop
      const quotaValue = vehicle ? await vehicle?.getQuotaValue(weeks, initialPaymentAmount) : null;
      const weeklyAmount = priceFormatFinancial(prices(quotaValue?.paymentWeek), 0);
      const valueBeforeDiscount = vehicle?.getWeeklyPriceWithoutDiscount(weeks) ?? 0;
      PlanArr.push({
        title,
        weeks,
        weeklyAmount,
        weeklyAmountValue: quotaValue?.paymentWeek ?? 0,
        noDiscountAmount: valueBeforeDiscount > 0 ? valueBeforeDiscount.toString() : undefined,
        depositAmount: calculateDeposit(quotaValue?.loanAmount ?? 0)
      });
    }
    setSelectPlanArr(PlanArr);
  };

  useEffect(() => {
    updateSelectPlanArr();

  }, [vehicle, initialPaymentAmount]);

  const handlePlanClick = (index: number, weeks: number = 52) => {
    setSelectedPlanIndex(index);
  };

  if (loading) return <div/>;

  return (
    <div>
      {!Opened ? (
        <Typography
          scale="medium"
          weight="600"
          className="m_y_xs"
        >
          Selecciona tu forma de pago:
        </Typography>
      ) : (
        <div className="display_flex flex_col flex_gap_xs">
          <div className="display_flex flex_align_center flex_justify_between_desktop flex_gap_xs_mobile flex_col_mobile">
            <Typography
              scale="small"
              weight="400"
              className="m_y_xs text_center_mobile"
            >
              ¿Quieres dar{" "}
              <span style={{ fontWeight: "bold" }}>
                un enganche?
              </span>{" "}
              <span className="text_neutral_500">(opcional)</span>
            </Typography>
            <div className="w_100_per_mobile w">
              <Input
                type="number"
                style={{ textAlign: "center" }}
                title=""
                placeholder=""
                name="input-slider"
                onChange={onInputChange}
                value={advancedMoney === 0 ? "" : advancedMoney}
              />
            </div>
          </div>
          <div className="bg_neutral_200 br_xxs p_md display_flex flex_gap_md_desktop flex_gap_xl_mobile flex_center m_b_md_mobile">
            <div
              className="display_flex flex_center bg_neutral_0 text_primary_300 br_xxs  cursor_pointer dim_25_px"
              onClick={handleButtonRest}
              style={{
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                msUserSelect: "none",
                userSelect: "none",
              }}
            >
              -
            </div>
            <div className="w_70_per_desktop w_60_per_mobile">
              <Slider
                valueLabelDisplay="auto"
                value={advancedMoney}
                onChange={handleSliderChange}
                aria-labelledby="slider"
                min={min}
                max={max}
                step={step}
                sx={{
                  color: "var(--primary_300) !important",
                  height: "10",
                  padding: "15px 0",
                  "& .MuiSlider-thumb": {
                    height: 18,
                    width: 18,
                    backgroundColor: "var(--primary_400)",
                    boxShadow: BoxShadow,
                    "&:focus, &:hover, &.Mui-active": {
                      boxShadow:
                        "0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.3),0 0 0 1px rgba(0,0,0,0.02)",
                      // Reset on touch devices, it doesn't add specificity
                      "@media (hover: none)": {
                        boxShadow: BoxShadow,
                      },
                    },
                  },

                  "& .MuiSlider-track": {
                    border: "none",
                    height: 8,
                  },
                  "& .MuiSlider-rail": {
                    opacity: 0.5,
                    height: 8,
                    backgroundColor: "white",
                    border: "1px solid #C7CCD1",
                    borderRadius: 8,
                  },
                  "& .MuiSlider-markLabel": {
                    marginTop: 7,
                    "&.MuiSlider-markLabelActive": {
                      opacity: 1,
                      color: "rgba(var(--primary_400),0.9)",
                    },
                  },
                  "& .MuiSlider-mark": {
                    backgroundColor: "#bfbfbf",
                    height: 8,
                    width: 2,
                    marginTop: 12,
                    "&.MuiSlider-markActive": {
                      opacity: 1,
                      backgroundColor: "var(--primary_400)",
                    },
                  },
                }}
              />
            </div>
            <div
              className="display_flex flex_center bg_neutral_0 text_primary_300 br_xxs  cursor_pointer dim_25_px"
              onClick={handleButtonSum}
              style={{
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                msUserSelect: "none",
                userSelect: "none",
              }}
            >
              +
            </div>
          </div>
        </div>
      )}

      {selectPlanArr?.map((item, idx) => (
        <PlanSelectionBox
          title={item.title}
          key={`${item.weeks}-${idx}`}
          weeks={item.weeks}
          weeklyAmount={item.weeklyAmount}
          handlePlanClick={handlePlanClick}
          index={idx}
          selectedPlanIndex={selectedPlanIndex}
          noDiscountAmount={item.noDiscountAmount}
        />
      ))}

      {!Opened && (
        <div
          style={{
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
            userSelect: "none",
          }}
        >
          <Typography
            scale="small"
            weight="600"
            className="m_t_xl m_b_xs"
          >
            Haz que tu plan sea más barato
          </Typography>
          <div
            className="bg_neutral_200 display_flex flex_gap_xs p_md br_xs cursor_pointer"
            onClick={handleButtonAM}
          >
            <AddCircle className="text_neutral_1000" />
            <Typography scale="small" weight="600" className="">
              Agregar enganche
            </Typography>
            <Typography
              scale="small"
              weight="600"
              className="text_neutral_600"
            >
              (Opcional)
            </Typography>
          </div>
        </div>
      )}

      {selectPlanArr.length > 0 && selectedPlanIndex >= 0 && <SummaryBox
        title={selectPlanArr[selectedPlanIndex].title}
        deposit={selectPlanArr[selectedPlanIndex].depositAmount}
        quotaValue={selectPlanArr[selectedPlanIndex].weeklyAmountValue}
      />}

      <div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: !matchesXS ? "" : "flex-end",
          }}
        >
          <Button
            className="w_100_per m_y_md"
            scale="small"
            onClick={openFinancialForm}
          >
            <Typography scale="medium" weight="600" className="flex_center">
              Continuar con mi finacimiento <ChevronRight />
            </Typography>
          </Button>
        </div>
      </div>
      <Modal open={OpenAM} setOpen={handleModalAM} className="modalAM">
        <div className="display_flex flex_col flex_gap_xs">
          <div className="display_flex flex_align_center flex_justify_between_desktop flex_gap_xs_mobile flex_col_mobile">
            <Typography
              scale="small"
              weight="400"
              className="m_y_xs text_center_mobile"
            >
              ¿Quieres dar{" "}
              <span style={{ fontWeight: "bold" }}>
                un enganche?
              </span>{" "}
              <span className="text_neutral_500">(opcional)</span>
            </Typography>
            <div className="w_100_per_mobile w">
              <Input
                type="number"
                style={{ textAlign: "center" }}
                title=""
                placeholder=""
                name="input-slider"
                onChange={onInputChange}
                value={advancedMoney === 0 ? "" : advancedMoney}
              />
            </div>
          </div>
          <div className="bg_neutral_200 br_xxs p_md display_flex flex_gap_md_desktop flex_gap_xl_mobile flex_center">
            <div
              className="display_flex flex_center bg_neutral_0 text_primary_300 br_xxs  cursor_pointer dim_25_px"
              onClick={handleButtonRest}
              style={{
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                msUserSelect: "none",
                userSelect: "none",
              }}
            >
              -
            </div>
            <div className="w_70_per_desktop w_60_per_mobile">
              <Slider
                valueLabelDisplay="auto"
                value={advancedMoney}
                onChange={handleSliderChange}
                aria-labelledby="slider"
                min={min}
                max={max}
                step={step}
                sx={{
                  color: "var(--primary_300) !important",
                  height: "10",
                  padding: "15px 0",
                  "& .MuiSlider-thumb": {
                    height: 18,
                    width: 18,
                    backgroundColor: "var(--primary_400)",
                    boxShadow: BoxShadow,
                    "&:focus, &:hover, &.Mui-active": {
                      boxShadow:
                        "0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.3),0 0 0 1px rgba(0,0,0,0.02)",
                      // Reset on touch devices, it doesn't add specificity
                      "@media (hover: none)": {
                        boxShadow: BoxShadow,
                      },
                    },
                  },

                  "& .MuiSlider-track": {
                    border: "none",
                    height: 8,
                  },
                  "& .MuiSlider-rail": {
                    opacity: 0.5,
                    height: 8,
                    backgroundColor: "white",
                    border: "1px solid #C7CCD1",
                    borderRadius: 8,
                  },
                  "& .MuiSlider-markLabel": {
                    marginTop: 7,
                    "&.MuiSlider-markLabelActive": {
                      opacity: 1,
                      color: "rgba(var(--primary_400),0.9)",
                    },
                  },
                  "& .MuiSlider-mark": {
                    backgroundColor: "#bfbfbf",
                    height: 8,
                    width: 2,
                    marginTop: 12,
                    "&.MuiSlider-markActive": {
                      opacity: 1,
                      backgroundColor: "var(--primary_400)",
                    },
                  },
                }}
              />
            </div>
            <div
              className="display_flex flex_center bg_neutral_0 text_primary_300 br_xxs  cursor_pointer dim_25_px"
              onClick={handleButtonSum}
              style={{
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                msUserSelect: "none",
                userSelect: "none",
              }}
            >
              +
            </div>
          </div>
        </div>

        <Typography scale="small" weight="600" className="m_y_xs">
          Visualiza tus descuento{" "}
          <span className="text_primary_300">en tiempo real</span>
        </Typography>
        {selectPlanArr?.map((item, idx) => (
          <PlanSelectionBox
            title={item.title}
            key={`${item.weeks}-${idx}-normal`}
            weeks={item.weeks}
            weeklyAmount={item.weeklyAmount}
            handlePlanClick={handlePlanClick}
            index={idx}
            selectedPlanIndex={selectedPlanIndex}
            noDiscountAmount={item.noDiscountAmount}
          />
        ))}
        <div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: !matchesXS ? "" : "flex-end",
            }}
          >
            <Button
              className="w_100_per m_y_md"
              scale="small"
              onClick={openFinancialForm}
            >
              <Typography scale="medium" weight="600" className="flex_center">
                Continuar con mi finacimiento <ChevronRight />
              </Typography>
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SummaryPayment;
